<template>
	<b-overlay :show="!!(userData.id && !userData.is_pro)" variant="dark" opacity="0.75">
		<template #overlay>
			<div class="text-center p-4">
				<p class="mb-3">The ban reaper widget is available for Pro subscribers only.</p>
				<b-button variant="warning" @click="$root.$emit('show-upgrade-modal')">Upgrade</b-button>
			</div>
		</template>
	<widget-settings-layout ref="layout" v-if="settings" slug="banReaper" name="Ban Reaper" :error="saveError" :saving="saving" :has-unsaved-changes="hasUnsavedChanges" :settings="settings" :saved-settings-json="savedSettingsJson" @save="save">
		<b-card class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Fluid limit" description="Maximum blood particles on screen at once">
						<b-input type="range" v-model.number="settings.maxParticles" min="200" max="2500" step="100"></b-input>
						<div class="text-muted small text-right">{{ settings.maxParticles }}</div>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Bleed time" description="How long a cut keeps bleeding">
						<b-input type="range" v-model.number="settings.bleedSeconds" min="0" max="10" step="0.5"></b-input>
						<div class="text-muted small text-right">{{ settings.bleedSeconds.toFixed(1) }}s</div>
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-checkbox v-model="settings.blurEnabled">Blur (gooey blood)</b-form-checkbox>
		</b-card>
		<b-card title="Text" class="mb-3">
			<b-form-row>
				<b-col cols="8">
					<div class="form-group">
						<div class="float-right"><b-form-checkbox size="sm" v-model="settings.customFont">Custom</b-form-checkbox></div>
						<label for="fontFamilyInput">Font family</label>
						<VueTypeaheadBootstrap
							v-model="settings.fontFamily"
							:data="settings.customFont ? [] : googleFonts"
							:serializer="item => `${item.family}`"
							placeholder="Search fonts..."
							disable-sort
							autocomplete="off"
							required
							id="fontFamilyInput"
						/>
					</div>
				</b-col>
				<b-col>
					<b-form-group label="Font size" append="px" description="0 = default per label">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="999" step="any" v-model="settings.fontSize"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card title="Outline" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Text outline blur">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="100" step="any" v-model="settings.blurRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Text outline size">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="10" step="any" v-model="settings.spreadRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card title="Colors" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Text Color" description="Chatter username">
						<color-picker v-model="settings.textColor" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Text Outline Color">
						<color-picker v-model="settings.textOutlineColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Reaper Name Color">
						<color-picker v-model="settings.reaperNameColor" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Reaper Name Outline Color">
						<color-picker v-model="settings.reaperNameOutlineColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Reaper Kill Count Color">
						<color-picker v-model="settings.reaperKillCountColor" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Reaper Kill Count Outline Color">
						<color-picker v-model="settings.reaperKillCountOutlineColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card class="mb-3">
			<h5 class="card-title">Default reaper</h5>
			<p class="text-muted small">Used whenever a ban's moderator isn't listed below, or isn't known.</p>
			<label class="small text-muted mb-1">Idle / approach</label>
			<PipiPupuFileS3 v-model="settings.defaultReaperImage" endpoint="banReaperImage" accept="image/*" preview placeholder="Choose or drop an image/GIF…" />
			<b-form-checkbox v-model="settings.idlePingPong" class="mt-1">Ping-pong idle animation (play forward then backward, instead of restarting)</b-form-checkbox>
			<label class="small text-muted mb-1 mt-2">Slash animation</label>
			<PipiPupuFileS3 v-model="settings.defaultReaperSlashImage" endpoint="banReaperImage" accept="image/*" preview placeholder="Choose or drop an image/GIF…" />
			<b-form-row class="mt-2">
				<b-col>
					<b-form-group label="Reaper X">
						<b-input-group append="px">
							<b-form-input type="number" step="any" v-model.number="settings.reaperX"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Reaper Y">
						<b-input-group append="px">
							<b-form-input type="number" step="any" v-model.number="settings.reaperY"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Reaper Size">
						<b-input-group append="px">
							<b-form-input type="number" min="1" step="any" v-model.number="settings.reaperSize"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Avatar X">
						<b-input-group append="px">
							<b-form-input type="number" step="any" v-model.number="settings.avatarX"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Avatar Y">
						<b-input-group append="px">
							<b-form-input type="number" step="any" v-model.number="settings.avatarY"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Avatar Size">
						<b-input-group append="px">
							<b-form-input type="number" min="1" step="any" v-model.number="settings.avatarSize"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col cols="4">
					<b-form-group label="Slash cut delay" description="Time after the slash starts before the avatar is cut. Blank = when the animation ends.">
						<b-input-group append="ms">
							<b-form-input type="number" min="0" step="any" placeholder="At end" v-model.number="settings.slashCutDelay"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card v-for="(mod, i) in settings.moderators" :key="i" class="mb-3">
			<div class="d-flex justify-content-between align-items-center mb-2">
				<strong class="small text-muted">Moderator {{ i + 1 }}</strong>
				<b-button size="sm" variant="link" class="text-danger p-0" @click="removeModerator(i)"><b-icon icon="x-circle"></b-icon></b-button>
			</div>
			<b-form-row>
				<b-col cols="6">
					<b-form-input v-model="mod.id" placeholder="Moderator id"></b-form-input>
				</b-col>
				<b-col cols="6">
					<b-form-input v-model="mod.username" placeholder="Moderator username"></b-form-input>
				</b-col>
			</b-form-row>
			<label class="small text-muted mb-1 mt-2">Reaper image</label>
			<PipiPupuFileS3 v-model="mod.reaperImage" endpoint="banReaperImage" accept="image/*" preview placeholder="Choose or drop an image/GIF…" />
			<label class="small text-muted mb-1 mt-2">Slash animation</label>
			<PipiPupuFileS3 v-model="mod.reaperSlashImage" endpoint="banReaperImage" accept="image/*" preview placeholder="Choose or drop an image/GIF…" />
			<b-form-row class="mt-2">
				<b-col cols="4">
					<b-form-group label="Offset X" class="mb-0">
						<b-input-group append="px">
							<b-form-input type="number" step="any" v-model.number="mod.offsetX"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col cols="4">
					<b-form-group label="Offset Y" class="mb-0">
						<b-input-group append="px">
							<b-form-input type="number" step="any" v-model.number="mod.offsetY"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col cols="4">
					<b-form-group label="Reaper Size" class="mb-0">
						<b-input-group append="px">
							<b-form-input type="number" min="1" step="any" placeholder="Default" v-model.number="mod.reaperSize"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row class="mt-2">
				<b-col cols="4">
					<b-form-group label="Slash cut delay" class="mb-0" description="Blank = default">
						<b-input-group append="ms">
							<b-form-input type="number" min="0" step="any" placeholder="Default" v-model.number="mod.slashCutDelay"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<p v-if="!settings.moderators.length" class="text-muted small">No moderators configured yet — bans always use the default reaper until you add one.</p>
		<b-card class="mb-3">
			<h5 class="mb-2">Moderator reapers</h5>
			<p class="text-muted small">Give a specific moderator their own reaper images, matched by id or username when they issue a ban.</p>
			<b-button variant="secondary" block @click="addModerator">Add moderator</b-button>
		</b-card>
		<template #save-extra>
			<b-button variant="secondary" class="ml-2" @click="testBan">Test ban</b-button>
		</template>
		<template #preview>
			<ban-reaper-widget-preview :settings="settings" ref="preview" />
		</template>
	</widget-settings-layout>
	</b-overlay>
</template>

<script>
import VueTypeaheadBootstrap from 'vue-typeahead-bootstrap';
import ColorPicker from '@/components/ColorPicker.vue';
import WidgetSettingsLayout from '@/components/Widget/WidgetSettingsLayout.vue';
import BanReaperWidgetPreview from '@/components/Widget/BanReaperWidgetPreview.vue';
import PipiPupuFileS3 from '@/components/PipiPupuFileS3.vue';

export default {
	beforeRouteLeave(to, from, next) {
		if (!this.hasUnsavedChanges) return next();
		this.$refs.layout.showLeaveModal(next, () => this.save());
	},
	data() { return {
		loading: 0,
		error: null,
		saving: 0,
		saveError: null,
		settings: null,
		userData: {},
		savedSettingsJson: null,
		googleFonts: [],
	}; },
	components: {
		VueTypeaheadBootstrap,
		ColorPicker,
		WidgetSettingsLayout,
		BanReaperWidgetPreview,
		PipiPupuFileS3,
	},
	async mounted() {
		window.addEventListener('beforeunload', this._onBeforeUnload);
		try {
			this.loading++;
			this.error = null;
			this.userData = await this.$api.get('userData');
			const settings = await this.$api.get('widgetSettings/ban-reaper');
			this.settings = {
				...settings,
				maxParticles: settings.maxParticles || 900,
				bleedSeconds: settings.bleedSeconds != null ? settings.bleedSeconds : 1,
				slashCutDelay: settings.slashCutDelay != null ? settings.slashCutDelay : '',
				blurEnabled: settings.blurEnabled !== false,
				idlePingPong: settings.idlePingPong || false,
				avatarX: settings.avatarX != null ? settings.avatarX : 480,
				avatarY: settings.avatarY != null ? settings.avatarY : 300,
				avatarSize: settings.avatarSize != null ? settings.avatarSize : 92,
				reaperX: settings.reaperX != null ? settings.reaperX : 480,
				reaperY: settings.reaperY != null ? settings.reaperY : 520,
				reaperSize: settings.reaperSize != null ? settings.reaperSize : 120,
				defaultReaperImage: settings.defaultReaperImage || null,
				defaultReaperSlashImage: settings.defaultReaperSlashImage || null,
				moderators: (settings.moderators || []).map(m => ({
					id: m.id || '',
					username: m.username || '',
					reaperImage: m.reaperImage || null,
					reaperSlashImage: m.reaperSlashImage || null,
					offsetX: m.offsetX || 0,
					offsetY: m.offsetY || 0,
					reaperSize: m.reaperSize || '',
					slashCutDelay: m.slashCutDelay != null ? m.slashCutDelay : '',
				})),
				fontFamily: settings.fontFamily || '',
				fontSize: settings.fontSize || 0,
				customFont: settings.customFont || false,
				blurRadius: settings.blurRadius || 0,
				spreadRadius: settings.spreadRadius || 0,
				textColor: settings.textColor || 'rgba(232, 226, 212, 1)',
				textOutlineColor: settings.textOutlineColor || 'rgba(0, 0, 0, 1)',
				reaperNameColor: settings.reaperNameColor || 'rgba(232, 226, 212, 1)',
				reaperNameOutlineColor: settings.reaperNameOutlineColor || 'rgba(0, 0, 0, 1)',
				reaperKillCountColor: settings.reaperKillCountColor || 'rgba(216, 31, 36, 1)',
				reaperKillCountOutlineColor: settings.reaperKillCountOutlineColor || 'rgba(0, 0, 0, 1)',
			};
			this.savedSettingsJson = JSON.stringify(this.settings);
			this.googleFonts = await this.$api.get('googleFonts');
		} catch(e) {
			this.error = e;
		} finally {
			this.loading--;
		}
	},
	computed: {
		hasUnsavedChanges() {
			if (this.savedSettingsJson === null) return false;
			return JSON.stringify(this.settings) !== this.savedSettingsJson;
		},
	},
	beforeDestroy() {
		window.removeEventListener('beforeunload', this._onBeforeUnload);
	},
	methods: {
		_onBeforeUnload(e) {
			if (this.hasUnsavedChanges) {
				e.preventDefault();
				e.returnValue = '';
			}
		},
		addModerator() {
			this.settings.moderators.push({ id: '', username: '', reaperImage: null, reaperSlashImage: null, offsetX: 0, offsetY: 0, reaperSize: '', slashCutDelay: '' });
		},
		removeModerator(i) {
			this.settings.moderators.splice(i, 1);
		},
		testBan() {
			this.$refs.preview?.fireTestBan();
		},
		async save() {
			try {
				this.saving++;
				this.saveError = null;

				await this.$api.post('widgetSettings/ban-reaper', this.settings);
				this.savedSettingsJson = JSON.stringify(this.settings);
			} catch(e) {
				console.error('Error saving widget:', e);
				this.saveError = e;
			} finally {
				this.saving--;
			}
		},
	},
};
</script>
